import { generatePath } from 'react-router-dom';
import { useUserStore } from '@Auth/store';
import MODALS from '@constants/modals';
import { useBlockFlags } from '@ContractBuilder/modules/block/context/context';
import { useEntityStore } from '@ContractBuilder/store';
import { useCommentsUIStore } from '@ContractBuilder/store/comments.store';
import { authProvider } from '@helpers/auth';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModalDispatch } from '@hooks/useModalProvider';
import type { ResourceBlock } from '@root/@types/base';
import Nav from '@src/routes';

import { deriveContractBlockActionsState } from '../utils/derive-contract-block-actions-state';
import type { ContractBlockActionsViewProps } from '../views/ContractBlockActionsView';

export const useContractBlockActions = (
  block: ResourceBlock,
): Omit<ContractBlockActionsViewProps, 'onDelete' | 'onEdit'> => {
  const {
    context,
    details: { isDeletable, isEditable },
  } = useBlockFlags();

  const { user } = useUserStore();
  const getFeatureFlagState = useFeatureFlags();
  const { showModal } = useModalDispatch();
  const submission = useEntityStore(({ submission }) => submission);
  const setActiveBlockId = useCommentsUIStore(({ setActiveBlockId }) => setActiveBlockId);

  const { id, blockLibraryId, type, canEditOnTemplate = true } = block;

  const displayState = deriveContractBlockActionsState({
    blockId: id,
    blockType: type,
    canEditOnTemplate,
    context,
    user,
    submission,
    isDeletable,
    isEditable,
    commentsFeatureEnabled: getFeatureFlagState('Comments'),
  });

  const handleOpenVisibilityModal = () => showModal(MODALS.BLOCK_VISIBILITY_MODAL, { blockId: id });
  const handleCommentClick = () => setActiveBlockId(id);

  const handleViewInLibrary = async () => {
    const searchParams = await authProvider.getAuthSearchParams();
    const to = generatePath(displayState.isClause ? Nav.Clause : Nav.Block, { id: blockLibraryId ?? '' });
    window.open(`${to}${searchParams}`);
  };

  return {
    block,
    ...displayState,
    onCommentClick: handleCommentClick,
    onViewInLibrary: handleViewInLibrary,
    onOpenVisibilityModal: handleOpenVisibilityModal,
  };
};
